import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_ORBIT, orbitBy, snapOrbit, type Orbit } from '../../../lib/preview-math.js';
import { renderOrbitUrl } from '../../../lib/thumbnails.js';
import { useZoomPan } from './useZoomPan.js';
import { ViewBar } from './ViewBar.js';

const SIZE = 512;
const CONTENT = { width: SIZE, height: SIZE };
const DEBOUNCE_MS = 140;

/**
 * Orbit / zoom viewer for a model. Route: each orbit position is a render of
 * the real Prism renderer (`prism model render --angle --pitch`, the same
 * auto-framing as the Model tab) fetched from the server; the previous angle
 * stays up until the new one has loaded. Drag orbits, wheel zooms (toward the
 * cursor), Shift/right drag pans, double click resets.
 */
export function ModelOrbit({ projectId, path, version, alt }: { projectId: number; path: string; version?: string; alt: string }) {
  const [orbit, setOrbit] = useState<Orbit>(DEFAULT_ORBIT);
  const [shown, setShown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requested = useRef<string | null>(null);

  const reset = useCallback(() => setOrbit(DEFAULT_ORBIT), []);
  const onDrag = useCallback((dx: number, dy: number) => setOrbit((o) => orbitBy(o, dx, dy)), []);
  const zoom = useZoomPan({ content: CONTENT, onDrag, onReset: reset });

  // Debounced: fetch the render of the snapped orbit, swap it in once decoded.
  const snapped = snapOrbit(orbit);
  const url = renderOrbitUrl(projectId, path, SIZE, snapped, version);
  useEffect(() => {
    if (requested.current === url) return;
    let live = true;
    const timer = setTimeout(() => {
      requested.current = url;
      setLoading(true);
      const img = new Image();
      img.onload = () => {
        if (!live) return;
        setShown(url);
        setError(false);
        setLoading(false);
      };
      img.onerror = () => {
        if (!live) return;
        requested.current = null;
        setError(true);
        setLoading(false);
      };
      img.src = url;
    }, shown === null ? 0 : DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const { view } = zoom;
  return (
    <div
      ref={zoom.boxRef}
      className={`gen-pv-box gen-pv-model${zoom.dragging ? ' is-dragging' : ''}`}
      role="application"
      aria-label={`${alt}. 3D preview: drag or arrow keys rotate, scroll or plus and minus zoom, Shift-drag pans, double click or 0 resets.`}
      {...zoom.boxProps}
    >
      {shown && (
        <img
          className="gen-pv-img"
          alt=""
          draggable={false}
          src={shown}
          style={{ width: SIZE, height: SIZE, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        />
      )}
      {error && <span className="muted gen-pv-msg">Could not render the model.</span>}
      {loading && (
        <span className="gen-pv-busy" role="status">
          Rendering...
        </span>
      )}
      <ViewBar onOut={() => zoom.zoomBy(0.8)} onIn={() => zoom.zoomBy(1.25)} onFit={zoom.fit} fitTitle="Reset view (double click)" />
    </div>
  );
}
